import { Theme } from '@mui/material/styles';

export const mobileThemeOverrides = (theme: Theme) => ({
  // Add global styles to prevent horizontal scrolling
  styleOverrides: {
    '@global': {
      'html, body': {
        margin: 0,
        padding: 0,
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      },
      '*': {
        boxSizing: 'border-box',
      },
    },
  },
  components: {
    // Mobile-optimized buttons
    MuiButton: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            minHeight: '44px',
            minWidth: '44px',
            fontSize: '0.875rem',
            padding: '8px 16px',
            borderRadius: '8px',
          },
          '@media (max-width: 480px)': {
            minHeight: '48px',
            minWidth: '48px',
            fontSize: '0.8rem',
            padding: '10px 18px',
            borderRadius: '10px',
          },
          '@media (max-width: 360px)': {
            minHeight: '52px',
            minWidth: '52px',
            fontSize: '0.75rem',
            padding: '12px 20px',
            borderRadius: '12px',
          },
        },
        sizeSmall: {
          '@media (max-width: 600px)': {
            minHeight: '40px',
            minWidth: '40px',
            fontSize: '0.8rem',
            padding: '6px 14px',
          },
          '@media (max-width: 480px)': {
            minHeight: '44px',
            minWidth: '44px',
            fontSize: '0.75rem',
            padding: '8px 16px',
          },
        },
        sizeLarge: {
          '@media (max-width: 600px)': {
            minHeight: '48px',
            minWidth: '48px',
            fontSize: '1rem',
            padding: '12px 20px',
          },
          '@media (max-width: 480px)': {
            minHeight: '52px',
            minWidth: '52px',
            fontSize: '0.9rem',
            padding: '14px 22px',
          },
        },
      },
    },

    // Mobile-optimized icon buttons
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            minHeight: '44px',
            minWidth: '44px',
            padding: '10px',
          },
          '@media (max-width: 480px)': {
            minHeight: '48px',
            minWidth: '48px',
            padding: '12px',
          },
        },
        sizeSmall: {
          '@media (max-width: 600px)': {
            minHeight: '40px',
            minWidth: '40px',
            padding: '8px',
          },
          '@media (max-width: 480px)': {
            minHeight: '44px',
            minWidth: '44px',
            padding: '10px',
          },
        },
        sizeLarge: {
          '@media (max-width: 600px)': {
            minHeight: '48px',
            minWidth: '48px',
            padding: '12px',
          },
          '@media (max-width: 480px)': {
            minHeight: '52px',
            minWidth: '52px',
            padding: '14px',
          },
        },
      },
    },

    // Mobile-optimized text fields
    MuiTextField: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '& .MuiOutlinedInput-root': {
              fontSize: '16px', // Prevent iOS zoom
              minHeight: '44px',
              '& input': {
                padding: '12px 14px',
              },
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
            },
          },
          '@media (max-width: 480px)': {
            '& .MuiOutlinedInput-root': {
              minHeight: '48px',
              '& input': {
                padding: '14px 16px',
              },
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.8rem',
            },
          },
        },
      },
    },

    // Mobile-optimized chips
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            height: '28px',
            fontSize: '0.75rem',
            '& .MuiChip-label': {
              padding: '0 8px',
            },
          },
          '@media (max-width: 480px)': {
            height: '32px',
            fontSize: '0.8rem',
            '& .MuiChip-label': {
              padding: '0 10px',
            },
          },
        },
        sizeSmall: {
          '@media (max-width: 600px)': {
            height: '24px',
            fontSize: '0.7rem',
            '& .MuiChip-label': {
              padding: '0 6px',
            },
          },
          '@media (max-width: 480px)': {
            height: '28px',
            fontSize: '0.75rem',
            '& .MuiChip-label': {
              padding: '0 8px',
            },
          },
        },
      },
    },

    // Mobile-optimized cards
    MuiCard: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            borderRadius: '12px',
            marginBottom: '12px',
            '& .MuiCardContent-root': {
              padding: '16px',
            },
          },
          '@media (max-width: 480px)': {
            borderRadius: '16px',
            marginBottom: '16px',
            '& .MuiCardContent-root': {
              padding: '20px',
            },
          },
        },
      },
    },

    // Mobile-optimized paper
    MuiPaper: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            borderRadius: '12px',
          },
          '@media (max-width: 480px)': {
            borderRadius: '16px',
          },
        },
      },
    },

    // Mobile-optimized app bar
    MuiAppBar: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '& .MuiToolbar-root': {
              minHeight: '56px',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
          },
          '@media (max-width: 480px)': {
            '& .MuiToolbar-root': {
              minHeight: '64px',
              paddingLeft: '20px',
              paddingRight: '20px',
            },
          },
        },
      },
    },

    // Mobile-optimized drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          '@media (max-width: 600px)': {
            width: '280px',
          },
          '@media (max-width: 480px)': {
            width: '300px',
          },
        },
      },
    },

    // Mobile-optimized menu
    MuiMenu: {
      styleOverrides: {
        paper: {
          '@media (max-width: 600px)': {
            minWidth: '180px',
            '& .MuiMenuItem-root': {
              minHeight: '44px',
              fontSize: '0.875rem',
            },
          },
          '@media (max-width: 480px)': {
            minWidth: '200px',
            '& .MuiMenuItem-root': {
              minHeight: '48px',
              fontSize: '0.9rem',
            },
          },
        },
      },
    },

    // Mobile-optimized dialog
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width: 600px)': {
            margin: '16px',
            maxWidth: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
          '@media (max-width: 480px)': {
            margin: '20px',
            maxWidth: 'calc(100% - 40px)',
            maxHeight: 'calc(100% - 40px)',
          },
        },
      },
    },

    // Mobile-optimized bottom navigation
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            height: '60px',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 0',
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                marginTop: '4px',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '22px',
              },
            },
          },
          '@media (max-width: 480px)': {
            height: '70px',
            '& .MuiBottomNavigationAction-root': {
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                marginTop: '6px',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '24px',
              },
            },
          },
        },
      },
    },

    // Mobile-optimized container
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            paddingLeft: '8px',
            paddingRight: '8px',
            maxWidth: '100vw',
            overflow: 'hidden',
          },
          '@media (max-width: 480px)': {
            paddingLeft: '12px',
            paddingRight: '12px',
          },
        },
      },
    },

    // Mobile-optimized grid
    MuiGrid: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '&.MuiGrid-container': {
              margin: 0,
              width: '100%',
            },
            '&.MuiGrid-item': {
              padding: '4px',
            },
          },
        },
      },
    },
  },
});
